import {
  BodyText,
  Field,
  FieldError,
  FieldLabel,
  Label,
  Separator,
  Switch,
} from "@/components/ui";
import { Search } from "@/components/ui";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "@tanstack/react-router";
import { useDebounce } from "react-use";

import { SubmitCancelButtons } from "@/features/Patient/MedicalRecords/components/SubmitCancelButtons";
import { useSnomedSearch } from "@/network/useOntologies";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { useSearchExternalProviders } from "../network/useExternalCareTeam";
import type { ExternalCareTeamMember } from "../network/useExternalCareTeam";
import { memberFormSchema } from "../schemas/schemas";

interface MemberFormProps {
  onSubmit(
    values: z.infer<typeof memberFormSchema>,
    existingMember?: ExternalCareTeamMember,
  ): void;
  onCancel(): void;
  isSaving?: boolean;
  existingMember?: ExternalCareTeamMember;
}

const SPECIALTY_PARENT_CODE = "223366009";

export const MemberForm = ({
  onSubmit,
  onCancel,
  isSaving = false,
  existingMember = undefined,
}: MemberFormProps) => {
  const { patientId } = useParams({ strict: false });
  const { data: patient } = useGetPatient(patientId);
  const patientHomeState = patient?.primaryAddress?.state;

  const form = useForm<z.infer<typeof memberFormSchema>>({
    resolver: zodResolver(memberFormSchema),
    defaultValues: {
      name: existingMember?.nameFullFormatted || undefined,
      npi: existingMember?.npi || undefined,
      specialtyCode: existingMember?.specialtyCode?.code || "",
      specialtyCodeDisplay: existingMember?.specialtyCode?.display || "",
      specialtyCodeSystem: existingMember?.specialtyCode?.system || "",
    },
  });

  const [nameSearchTerm, setNameSearchTerm] = useState<string>(
    existingMember?.nameFullFormatted || "",
  );

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  useDebounce(
    () => {
      setDebouncedSearchTerm(nameSearchTerm);
    },
    200,
    [nameSearchTerm],
  );

  const [specialtySearchTerm, setSpecialtySearchTerm] = useState<string>(
    existingMember?.specialtyCode.display || "",
  );

  const [searchOutsidePatientState, setSearchOutsidePatientState] =
    useState(false);

  const {
    data: externalProvidersData,
    isFetching: isFetchingExternalProviders,
  } = useSearchExternalProviders(
    debouncedSearchTerm,
    searchOutsidePatientState ? undefined : patientHomeState,
  );

  const { data: specialtyCodes, isFetching: isFetchingSpecialtyCodes } =
    useSnomedSearch({
      searchTerm: specialtySearchTerm,
      eclExpression: `<< ${SPECIALTY_PARENT_CODE}`,
    });

  const handleSubmit = (values: z.infer<typeof memberFormSchema>) => {
    onSubmit(
      {
        name: values.name,
        npi: values.npi || undefined,
        specialtyCode: values.specialtyCode,
        specialtyCodeDisplay: values.specialtyCodeDisplay,
        specialtyCodeSystem: values.specialtyCodeSystem,
      },
      existingMember,
    );
  };

  return (
    <>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex relative flex-col gap-2 px-3 py-2 w-full"
      >
        <Label>Add Care Team Member</Label>
        <div className="flex flex-col md:flex-row w-full gap-2">
          <Controller
            control={form.control}
            name="name"
            render={({ fieldState }) => (
              <Field className="w-full flex flex-1 flex-col">
                <FieldLabel htmlFor="name">
                  <BodyText size="sm" bold>
                    Name
                  </BodyText>
                </FieldLabel>
                <Search
                  placeholder="Search for a provider"
                  input={nameSearchTerm}
                  isLoading={isFetchingExternalProviders}
                  options={
                    externalProvidersData?.results?.map((provider) => ({
                      label: `${provider.firstName} ${provider.lastName} ${provider.credential || ""}`,
                      value: provider.npi,
                      subLabel: `${provider.city}, ${provider.state} NPI ${provider.npi}`,
                    })) || []
                  }
                  onInputChange={(input) => {
                    form.setValue("name", input);
                    form.setValue("npi", undefined);
                    setNameSearchTerm(input);
                  }}
                  onItemSelected={(item) => {
                    form.setValue("name", item.label, {
                      shouldValidate: true,
                    });
                    if (!item.isFreeText) {
                      form.setValue("npi", item.value.toString());
                    } else {
                      form.setValue("npi", undefined);
                    }
                  }}
                  allowFreeText={true}
                  alwaysShowFreetextOption
                  noResultsText=""
                  shouldFilter={false}
                  openOnFocus
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <Controller
            control={form.control}
            name="specialtyCode"
            render={({ fieldState }) => (
              <Field className="w-full flex flex-1 flex-col">
                <FieldLabel htmlFor="specialtyCode">
                  <BodyText size="sm" bold>
                    Specialty
                  </BodyText>
                </FieldLabel>
                <Search
                  placeholder="Search for a specialty"
                  input={specialtySearchTerm}
                  isLoading={isFetchingSpecialtyCodes}
                  options={
                    specialtyCodes?.map((code) => ({
                      label: code.display,
                      value: code.code,
                      metadata: {
                        system: code.system,
                      },
                    })) || []
                  }
                  onInputChange={(searchTerm) => {
                    form.setValue("specialtyCode", "");
                    form.setValue("specialtyCodeDisplay", "");
                    form.setValue("specialtyCodeSystem", "");
                    setSpecialtySearchTerm(searchTerm);
                  }}
                  onItemSelected={(item) => {
                    form.setValue("specialtyCode", item.value.toString(), {
                      shouldValidate: true,
                    });
                    form.setValue("specialtyCodeDisplay", item.label);
                    form.setValue(
                      "specialtyCodeSystem",
                      (item.metadata as { system?: string })?.system || "",
                    );
                  }}
                  resetSelectedOnBlur={true}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
        {patientHomeState && (
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <Field className="w-full">
              <FieldLabel htmlFor="searchOutsidePatientState">
                <BodyText size="sm" bold>
                  Search outside of {patientHomeState}?
                </BodyText>
              </FieldLabel>
              <Switch
                checked={searchOutsidePatientState}
                onCheckedChange={setSearchOutsidePatientState}
              />
            </Field>
          </div>
        )}
        <SubmitCancelButtons isSaving={isSaving} onCancel={onCancel} />
      </form>
      <Separator />
    </>
  );
};
